import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User, UpdateProfileRequest, ChangePasswordRequest } from '../../models';


import { UserService } from '../../../core/services/user.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../../core/services/auth.service';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { DividerModule } from '@openng/optimus-ui/divider';
import { PasswordModule } from '@openng/optimus-ui/password';
import { FieldsetModule } from '@openng/optimus-ui/fieldset';

interface UserProfileForm {
  fullName?: string;
  position?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

@Component({
  selector: 'app-user-profile',
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, DividerModule, PasswordModule, FieldsetModule],
  templateUrl: './user-profile.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit {
  @Input() user: User | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);

  formData: UserProfileForm = {};
  loading = false;
  formErrors: { [key: string]: string } = {};

  ngOnInit(): void {
    if (this.user) {
      this.formData = {
        fullName: this.user.fullName || '',
        position: this.user.position || '',
      };
      this.cdr.markForCheck();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  async onSave(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    try {
      // Update profile data
      if (this.hasProfileChanges()) {
        const profileData: UpdateProfileRequest = {
          fullName: this.formData.fullName,
          position: this.formData.position,
        };

        await this.userService.updateProfile(profileData).toPromise();
      }

      // Change password if provided
      if (this.formData.currentPassword && this.formData.newPassword) {
        const passwordData: ChangePasswordRequest = {
          currentPassword: this.formData.currentPassword,
          newPassword: this.formData.newPassword
        };

        await this.userService.changePassword(passwordData).toPromise();
      }

      // Refresh current user data
      await this.authService.refreshCurrentUser();

      this.toastService.success('Profile updated successfully');
      this.save.emit();
      this.close.emit();

    } catch (error: any) {
      console.error('Profile update error:', error);
      this.toastService.error(error?.error?.message || 'Failed to update profile');
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  onLogout(): void {
    this.logout.emit();
  }



  private validateForm(): boolean {
    this.formErrors = {};
    let isValid = true;

    if (!this.formData.fullName?.trim()) {
      this.formErrors['fullName'] = 'Full name is required';
      isValid = false;
    }

    // Password validation - only if at least one password field is filled
    if (this.formData.currentPassword || this.formData.newPassword || this.formData.confirmPassword) {
      if (!this.formData.currentPassword) {
        this.formErrors['currentPassword'] = 'Current password is required to change password';
        isValid = false;
      }

      if (!this.formData.newPassword) {
        this.formErrors['newPassword'] = 'New password is required';
        isValid = false;
      } else if (this.formData.newPassword.length < 6) {
        this.formErrors['newPassword'] = 'Password must be at least 6 characters';
        isValid = false;
      }

      if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.formErrors['confirmPassword'] = 'Passwords do not match';
        isValid = false;
      }
    }

    return isValid;
  }

  private hasProfileChanges(): boolean {
    if (!this.user) return false;
    
    return (
      this.formData.fullName !== this.user.fullName ||
      this.formData.position !== this.user.position
    );
  }
}
